import { EventEmitter, Injectable, Optional } from '@angular/core';
import { UUID } from 'angular2-uuid';
import {
  LastShiftTotalsResponse,
  OfflineSessionState,
  TaxJournalReplacement,
  TaxRegisterContext as CoreTaxRegisterContext,
  TaxRegisterContextId,
  TaxRegisterService,
  TaxRegisterState,
  TaxServerState,
  TransactionsRegistrarStateResponse,
} from 'tax-register-core';
import { CompanyStorage } from 'src/app/_core/company-storage';
import { MonitoringService } from 'src/app/services/monitoring.service';
import { SettingsService } from 'src/app/services/settings.service';
import type { ITaxConfig } from './tax-register-config.service';

export type {
  OfflineSessionState,
  StoredTaxServerState,
  TaxRegisterContextId,
  TaxServerState,
} from 'tax-register-core';

export type TaxRegisterContextState = TaxRegisterState;
export type TaxRegisterContext = CoreTaxRegisterContext<ITaxConfig>;

// An error of the register's offline work, kept for the diagnostics view.
export interface TaxOfflineSessionError {
  message: string;
  occurredAt: string;
  operation?: string;
}

// Link from a store to the tax register it fiscalizes with. The key is the one used before
// the register state moved to the shared package, so existing links keep working.
const TAX_REGISTER_STORE_KEY_PREFIX = 'TAX_REGISTER_STORE__';
const TAX_REGISTER_OFFLINE_ERRORS_KEY_PREFIX = 'TAX_REGISTER_OFFLINE_ERRORS__';
const MAX_OFFLINE_SESSION_ERRORS = 20;

// Application Insights severity level of the tax register logger.
const SEVERITY_WARNING = 2;

// Angular binding of the shared tax register service: company-scoped storage, the offline
// settings from the remote configuration, state change events, the application's link between
// stores and registers, and the register's offline errors, logged to the application's tax
// register logger and kept for the diagnostics view.
@Injectable({ providedIn: 'root' })
export class TaxRegisterContextService extends TaxRegisterService<ITaxConfig> {
  // Emits the register's fiscal number after every change made through this service.
  readonly stateChanged = new EventEmitter<string>();

  constructor(
    private _companyStorage: CompanyStorage,
    @Optional() settings?: SettingsService,
    @Optional() private _monitoring?: MonitoringService,
  ) {
    super(
      _companyStorage,
      () => ({
        ...settings?.taxOfflineSettings,
        useServerTimeCorrection: !!settings?.usePrroServerTimeCorrection,
      }),
      // The WebView may not provide crypto.randomUUID (no secure context).
      { createUid: () => UUID.UUID() },
    );
  }

  // Links the config's store to the register once its context is initialized. A new snapshot
  // starts without the errors of the previous offline work.
  override async initContext(
    taxConfig: ITaxConfig,
    registerState: TransactionsRegistrarStateResponse,
    lastShiftTotals: LastShiftTotalsResponse | null,
    serverState: TaxServerState,
    force = false,
  ): Promise<boolean> {
    const storeId = this._normalizeStoreId(taxConfig?.store?.erpStoreId);
    const initialized = await super.initContext(
      taxConfig,
      registerState,
      lastShiftTotals,
      serverState,
      force,
    );
    if (initialized) {
      await this._companyStorage.remove(
        this._getOfflineErrorsKey(taxConfig.taxRegister.fiscalNumber),
      );
      await this._linkStore(storeId, taxConfig.taxRegister.fiscalNumber);
      this._emit(taxConfig.taxRegister.fiscalNumber);
    }

    return initialized;
  }

  // Also removes the register's offline errors and the store's link when it points to this
  // register.
  override async removeContext(
    taxRegisterId: TaxRegisterContextId,
  ): Promise<void> {
    const context = await this.getTaxContext(taxRegisterId).catch(() => null);
    await super.removeContext(taxRegisterId);
    await this._companyStorage.remove(this._getOfflineErrorsKey(taxRegisterId));

    // Contexts saved before the move to the package also carry the store id directly.
    const storeId =
      context?.taxConfig?.store?.erpStoreId ??
      (context as { storeId?: number } | null)?.storeId;
    if (storeId != null) {
      const linkedId = await this.getTaxRegisterIdByStoreId(storeId);
      if (linkedId === this._toTaxRegisterId(taxRegisterId)) {
        await this._companyStorage.remove(this._getStoreKey(storeId));
      }
    }
    this._emit(taxRegisterId);
  }

  override async saveDocument(
    taxRegisterId: TaxRegisterContextId,
    rawXml: string,
    ticketXml?: string | null,
  ): Promise<string> {

    console.log(rawXml);;
    console.log(ticketXml);
    const xml = await super.saveDocument(taxRegisterId, rawXml, ticketXml);
    this._emit(taxRegisterId);
    return xml;
  }

  override async startOfflineSession(
    taxRegisterId: TaxRegisterContextId,
    originalXml: string,
  ): Promise<string> {
    const offlineXml = await super.startOfflineSession(
      taxRegisterId,
      originalXml,
    );
    this._emit(taxRegisterId);
    return offlineXml;
  }

  override async completeOfflineSession(
    taxRegisterId: TaxRegisterContextId,
    endSessionXml: string,
    nextSession: OfflineSessionState,
  ): Promise<void> {
    await super.completeOfflineSession(taxRegisterId, endSessionXml, nextSession);
    this._emit(taxRegisterId);
  }

  override async setDisabled(
    taxRegisterId: TaxRegisterContextId,
    disabled: boolean,
  ): Promise<void> {
    await super.setDisabled(taxRegisterId, disabled);
    this._emit(taxRegisterId);
  }

  override async setOfflineDisabled(
    taxRegisterId: TaxRegisterContextId,
    offlineDisabled: boolean,
  ): Promise<void> {
    await super.setOfflineDisabled(taxRegisterId, offlineDisabled);
    this._emit(taxRegisterId);
  }

  override async clearOfflineDocuments(
    taxRegisterId: TaxRegisterContextId,
  ): Promise<void> {
    await super.clearOfflineDocuments(taxRegisterId);
    this._emit(taxRegisterId);
  }

  override async unsafeStartOfflineSession(
    taxRegisterId: TaxRegisterContextId,
    originalXml: string,
    credentials?: OfflineSessionState,
  ): Promise<string> {
    const offlineXml = await super.unsafeStartOfflineSession(
      taxRegisterId,
      originalXml,
      credentials,
    );
    this._emit(taxRegisterId);
    return offlineXml;
  }

  override async unsafeReplaceDocuments(
    taxRegisterId: TaxRegisterContextId,
    changes: TaxJournalReplacement[],
  ): Promise<void> {
    await super.unsafeReplaceDocuments(taxRegisterId, changes);
    this._emit(taxRegisterId);
  }

  async getTaxRegisterIdByStoreId(storeId: number): Promise<string | null> {
    const linkedId = await this._companyStorage.get(this._getStoreKey(storeId));
    return linkedId == null ? null : this._toTaxRegisterId(linkedId) || null;
  }

  async getTaxRegisterByStoreId(
    storeId: number,
  ): Promise<ITaxConfig['taxRegister'] | null> {
    const context = await this.getTaxContextByStoreId(storeId);
    return context?.taxConfig?.taxRegister || null;
  }

  async getTaxContextByStoreId(
    storeId: number,
  ): Promise<TaxRegisterContext | null> {
    const taxRegisterId = await this.getTaxRegisterIdByStoreId(storeId);
    return taxRegisterId ? this.getTaxContext(taxRegisterId) : null;
  }

  // The registers with a context on the device: those linked to a store, disabled ones
  // included, and those of getAllTaxConfigs.
  async getTaxRegisterIds(): Promise<string[]> {
    const ids = new Set<string>();
    const keys: string[] = (await this._companyStorage.keys()) || [];
    for (const key of keys) {
      if (!key?.startsWith(TAX_REGISTER_STORE_KEY_PREFIX)) {
        continue;
      }
      const id = this._toTaxRegisterId(await this._companyStorage.get(key));
      if (id && (await this.hasContext(id))) {
        ids.add(id);
      }
    }
    for (const taxConfig of await this.getAllTaxConfigs()) {
      const id = this._toTaxRegisterId(taxConfig?.taxRegister?.fiscalNumber);
      if (id) {
        ids.add(id);
      }
    }
    return Array.from(ids);
  }

  async getOfflineSessionErrors(
    taxRegisterId: TaxRegisterContextId,
  ): Promise<TaxOfflineSessionError[]> {
    const errors = await this._companyStorage.get(
      this._getOfflineErrorsKey(taxRegisterId),
    );
    return Array.isArray(errors) ? [...errors] : [];
  }

  // Logs an error of the register's offline work and keeps the latest ones for diagnostics.
  async saveOfflineSessionError(
    taxRegisterId: TaxRegisterContextId,
    error: unknown,
    operation?: string,
  ): Promise<void> {
    const id = this._toTaxRegisterId(taxRegisterId);
    const message = toErrorMessage(error);
    try {
      this._monitoring
        ?.getTaxLogger()
        .logException(
          error instanceof Error ? error : new Error(message),
          SEVERITY_WARNING,
          { taxRegister: id, operation, message },
        );
    } catch {
      // A failing logger does not stop the error from being kept.
    }

    const errors = await this.getOfflineSessionErrors(id);
    errors.push({ message, occurredAt: new Date().toISOString(), operation });
    await this._companyStorage.set(
      this._getOfflineErrorsKey(id),
      errors.slice(-MAX_OFFLINE_SESSION_ERRORS),
    );
    this._emit(id);
  }

  private _emit(taxRegisterId: TaxRegisterContextId): void {
    this.stateChanged.emit(this._toTaxRegisterId(taxRegisterId));
  }

  private _getOfflineErrorsKey(taxRegisterId: TaxRegisterContextId): string {
    return `${TAX_REGISTER_OFFLINE_ERRORS_KEY_PREFIX}${this._toTaxRegisterId(taxRegisterId)}`;
  }

  private async _linkStore(
    storeId: number,
    taxRegisterId: TaxRegisterContextId,
  ): Promise<void> {
    const id = this._toTaxRegisterId(taxRegisterId);
    if ((await this.getTaxRegisterIdByStoreId(storeId)) === id) {
      return;
    }

    await this._companyStorage.set(this._getStoreKey(storeId), id);
  }

  private _getStoreKey(storeId: number): string {
    return `${TAX_REGISTER_STORE_KEY_PREFIX}${this._normalizeStoreId(storeId)}`;
  }

  private _normalizeStoreId(storeId: number): number {
    const id = Number(storeId);
    if (!Number.isInteger(id) || id < 0) {
      throw new Error('Store id is required.');
    }

    return id;
  }

  // Same normalization as the service applies to fiscal numbers.
  private _toTaxRegisterId(taxRegisterId: TaxRegisterContextId): string {
    return taxRegisterId == null ? '' : taxRegisterId.toString().trim();
  }
}

// The readable text of an error from the signer, the tax API or the service.
function toErrorMessage(error: unknown): string {
  if (error == null) {
    return String(error);
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message || error.name;
  }
  if (typeof error === 'object') {
    const details = error as {
      message?: string;
      error?: string;
      ResultText?: string;
      text?: string;
    };
    return (
      details.message ||
      details.error ||
      details.ResultText ||
      details.text ||
      JSON.stringify(error)
    );
  }

  return String(error);
}
